import { type ParsedResponse } from '@/types/yelp'

/**
 * Extracts the Yelp reservation link from the page data.
 *
 * @param {SanityPage} pageData
 * @returns { { url: string } | undefined }
 */
export const getDiningRoomLink = (pageData: SanityPage) => {
  return pageData?.locationData?.reservationLinks?.find((link: { url: string }) => link.url.includes('yelp.com'))
}

/**
 * Extracts the business ID from a Yelp URL.
 * Supports both reservation and business page URL formats.
 *
 * @param {string} url - The Yelp URL to parse
 * @returns {string | null} The extracted business ID or null if not found
 */
export const extractYelpBusinessId = (url: string): string | null => {
  const yelpUrlPattern = /yelp\.com\/(?:reservations|biz)\/([^/?#]+)/
  const match = url.match(yelpUrlPattern)
  return match ? match[1] : null
}

export const getLocationYelpData = (diningRoomLink?: string, yelpData?: ParsedResponse[]): ParsedResponse | null => {
  if (!diningRoomLink) return null

  const yelpId = extractYelpBusinessId(diningRoomLink || '')

  return yelpData?.find(item => item.businessId === yelpId) || null
}

/**
 * Extracts the first available wait time from waitlist estimates.
 * Checks estimates in order: 1-2, 3-4, 5-6, 7+ minutes
 *
 * @param {Record<string, string> | undefined} waitEstimates - Object with keys like '1-2', '3-4', etc.
 * @returns {string} The first available wait time or empty string if none found
 */
export const getFirstAvailableWaitTime = (
  waitEstimates?: Record<string, string>
): string => {
  if (!waitEstimates) return ''
  return (
    waitEstimates['1-2'] ||
    waitEstimates['3-4'] ||
    waitEstimates['5-6'] ||
    waitEstimates['7+'] ||
    ''
  )
}
